import { useBlocksConfig } from "@pathwright/blocks-engine"
import { BlockText } from "@pathwright/blocks/src/shared"
import {
  FilestackPreviewModal,
  useMuxEmbed
} from "@pathwright/legacy-media-manager"
import { MuxDownloadButton } from "@pathwright/media-client"
import { canPreview, createMediaDownloadURL } from "@pathwright/media-utils"
import Attachment from "@pathwright/ui/src/components/attachment/Attachment"
import { MEDIA_TYPES } from "@pathwright/ui/src/components/filestack/constants"
import { getPreviewURL } from "@pathwright/ui/src/components/filestack/utils"
import PropTypes from "prop-types"
import React from "react"
import { isMobile, removeHTML } from "../../../utils"
import { AttachmentListItemWrapper } from "../ui"

const AttachmentsListItem = (props) => {
  const {
    attachment,
    onUpdateAttachmentName,
    onRemoveAttachment,
    mode,
    domID
  } = props

  const [previewing, setPreviewing] = React.useState(false)
  const [isFocused, setIsFocused] = React.useState(false)

  const attachmentRef = React.useRef(null)

  const { theme, allowDownloadableVideos } = useBlocksConfig()
  const {
    embed: muxData, // from mux video HOC,
    loading: muxLoading,
    error: muxError
  } = useMuxEmbed() || {}

  if (muxLoading) return null
  if (muxError) {
    console.error(muxError)
    return null
  }

  const handleChangeName = (nextName) => {
    const nextAttachment = {
      ...attachment,
      name: removeHTML(nextName)
    }
    onUpdateAttachmentName(nextAttachment)
  }

  const handleRemove = (attachment) => () => {
    onRemoveAttachment(attachment)
  }

  const handleToggleModal = (toggle) => (event) => {
    const isDownloadClick = event.target.closest(
      ".Attachment__actions > a[download]"
    )
    // Skip modal if download link is clicked
    if (isDownloadClick) return

    // Return focus to attachment if modal is closed
    if (!toggle && !isFocused) {
      setTimeout(() => {
        attachmentRef.current.focus()
      })
    }

    setPreviewing(toggle)
  }

  const {
    name,
    url,
    thumb,
    mimetype,
    filestackHandle,
    mediaType,
    muxVideoID,
    fileSize
  } = attachment

  // Get thumbnail if available
  const thumbnail = thumb || (mediaType === MEDIA_TYPES.IMAGE ? url : "")

  // Get Mux download url from gql props

  const muxReadyURL = muxData?.download?.url

  // Get download url
  const downloadURL = createMediaDownloadURL(attachment, muxData)

  // Get preview url (default filestack url for certain file types)
  const previewURL =
    canPreview(mimetype) && filestackHandle
      ? getPreviewURL(filestackHandle)
      : null

  const canGenerateDownload = allowDownloadableVideos && !!muxVideoID
  const needsGenerateDownload = !downloadURL && !!muxVideoID

  const renderAttachment = ({ onClickDownload, isLoading } = {}) => {
    return (
      <Attachment
        name={name}
        mimetype={mimetype}
        thumbnail={thumbnail}
        url={downloadURL || url}
        download={!!downloadURL || canGenerateDownload}
        downloadOnly={(!!downloadURL || canGenerateDownload) && !previewURL}
        // If we don't have a download url for mux video,
        // we need to allow it to be generated on click
        onClickDownload={
          needsGenerateDownload
            ? (event) => {
                if (!downloadURL) event.stopPropagation()
                if (onClickDownload) onClickDownload(event)
              }
            : null
        }
        isLink={mediaType === MEDIA_TYPES.LINK}
        // requireConfirmation={false}
        onRemove={mode === "edit" ? handleRemove(attachment) : null}
        truncate={isMobile() ? 20 : 60} // On mobile shorten name gracefully
        mode={mode}
        fileSize={fileSize}
        loadingURL={!!muxVideoID && !muxReadyURL}
        overlay={true}
        isLoadingDownload={isLoading}
        renderNameView={
          mode === "view" ? ({ name }) => <BlockText html={name} /> : null
        }
        iconStyles={{
          borderRadius: "50%",
          backgroundColor: "white"
        }}
      />
    )
  }

  return (
    <>
      <AttachmentListItemWrapper
        isMobile={isMobile()}
        className={`AttachmentListItem${
          previewURL ? " AttachmentListItem--has-preview" : ""
        }`}
        onClick={handleToggleModal(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleToggleModal(true)(e)
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        tabIndex={0}
        id={domID}
        ref={attachmentRef}
      >
        {muxVideoID ? (
          <MuxDownloadButton
            id={muxVideoID}
            downloadURL={downloadURL}
            renderButton={({ onClick, isLoading }) =>
              renderAttachment({ onClickDownload: onClick, isLoading })
            }
          />
        ) : (
          renderAttachment()
        )}
      </AttachmentListItemWrapper>
      {previewURL && previewing ? (
        <FilestackPreviewModal
          onCloseModal={handleToggleModal(false)}
          previewHandle={filestackHandle}
          downloadURL={downloadURL}
          theme={theme}
        />
      ) : null}
    </>
  )
}

AttachmentsListItem.propTypes = {
  mode: PropTypes.oneOf(["view", "edit"]),
  onRemoveAttachment: PropTypes.func,
  attachment: PropTypes.object.isRequired,
  domID: PropTypes.string.isRequired
}

AttachmentsListItem.defaultProps = {
  mode: "view"
}

export default AttachmentsListItem
